import React from 'react';
import { TimePickerSettings } from '../../types';
import {
  generateValidTimeOptions,
  DEFAULT_PICKUP_TIME_SETTINGS,
} from '../../utils/timePickerDefaults';

interface TimeSelectorProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  roleOverride?: string;
  className?: string;
  // FL-59: 時間制限設定（オプショナル、未指定時はデフォルト設定を使用）
  timeSettings?: TimePickerSettings;
  // 時間間隔（分、デフォルトは5分）
  intervalMinutes?: number;
  // エラーメッセージ（FL-59: バリデーション機能）
  error?: string;
  // テスト用: 無効値を選択できるオプションを付与
  testAllowInvalidOption?: boolean;
}

export const TimeSelector: React.FC<TimeSelectorProps> = ({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  id,
  name,
  roleOverride,
  className = '',
  timeSettings = DEFAULT_PICKUP_TIME_SETTINGS,
  intervalMinutes = 5,
  error,
  testAllowInvalidOption = false,
}) => {
  // FL-59: 時間制限設定に基づいて有効な時間選択肢を生成
  const timeOptions = generateValidTimeOptions(timeSettings, intervalMinutes);
  const optionsToRender = React.useMemo(() => {
    const opts = [...timeOptions];
    if (value && !opts.includes(value)) {
      opts.unshift(value);
    }
    return opts;
  }, [timeOptions, value]);

  return (
    <div className={className}>
      <label
        htmlFor={id}
        className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
      >
        {label}
        {required && (
          <>
            {' '}
            <span className='text-red-500'>*</span>
          </>
        )}
      </label>
      <select
        id={id}
        name={name}
        role={roleOverride}
        aria-label={label}
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        className={`w-full px-4 py-3 border rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 min-h-[44px] touch-target ${
          error
            ? 'border-red-500 dark:border-red-400 focus:ring-red-500 focus:border-red-500'
            : 'border-gray-300 dark:border-gray-600'
        }`}
      >
        <option value=''>時間を選択</option>
        {optionsToRender.map(time => (
          <option key={time} value={time}>
            {time}
          </option>
        ))}
        {testAllowInvalidOption && <option value='invalid-time'>invalid-time</option>}
      </select>
      {error && <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{error}</p>}
    </div>
  );
};

export default TimeSelector;
